import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Notification, NotificationActionType } from '@drahft/api-client/types/notification';
import { API_ROUTES } from '@config/routes';
import api from '@services/api';
import { CACHE_DURATION as CACHE_TTL } from '../utils/cache';

interface NotificationState {
  items: Notification[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
  lastRequestedLimit: number;
}

const DEFAULT_NOTIFICATION_LIMIT = 100;

const initialState: NotificationState = {
  items: [],
  isLoading: false,
  error: null,
  lastFetched: null,
  lastRequestedLimit: 0,
};

const sortNotifications = (notifications: Notification[]) => {
  return [...notifications].sort((left, right) => {
    const leftTime = new Date(left.createdAt).getTime();
    const rightTime = new Date(right.createdAt).getTime();
    return rightTime - leftTime;
  });
};

const getNotificationStatusForAction = (
  actionType: NotificationActionType
): Notification['status'] => {
  if (actionType === 'dismiss') return 'dismissed';
  if (actionType === 'snooze') return 'snoozed';
  return 'actioned';
};

const applyNotificationActionState = (
  notification: Notification,
  actionType: NotificationActionType,
  serverNotification?: Notification
): Notification => {
  if (serverNotification) {
    return {
      ...notification,
      ...serverNotification,
      status: serverNotification.status ?? getNotificationStatusForAction(actionType),
      actionTaken: serverNotification.actionTaken ?? actionType,
    };
  }

  const actionedAt = new Date().toISOString();
  const snoozedUntil =
    actionType === 'snooze'
      ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      : notification.snoozedUntil;

  return {
    ...notification,
    status: getNotificationStatusForAction(actionType),
    actionedAt,
    actionTaken: actionType,
    snoozedUntil,
  };
};

export const fetchNotifications = createAsyncThunk<
  Notification[],
  { limit?: number; force?: boolean } | void,
  { state: { notifications: NotificationState } }
>('notifications/fetchNotifications', async (args, { getState, rejectWithValue }) => {
  const limit = args?.limit ?? DEFAULT_NOTIFICATION_LIMIT;
  const force = args?.force ?? false;
  const state = getState().notifications;

  if (
    !force &&
    state.items.length > 0 &&
    state.lastFetched &&
    Date.now() - state.lastFetched < CACHE_TTL &&
    state.lastRequestedLimit >= limit
  ) {
    return state.items;
  }

  try {
    const data = await api.get<Notification[]>(API_ROUTES.NOTIFICATIONS.BASE, {
      params: { limit },
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to load notifications');
  }
});

export const markNotificationRead = createAsyncThunk<
  { notificationId: string; readAt: string },
  { notificationId: string }
>('notifications/markNotificationRead', async ({ notificationId }, { rejectWithValue }) => {
  try {
    await api.post(API_ROUTES.NOTIFICATIONS.READ(notificationId));
    return { notificationId, readAt: new Date().toISOString() };
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to mark notification as read'
    );
  }
});

export const markAllNotificationsRead = createAsyncThunk<{ readAt: string }, void>(
  'notifications/markAllNotificationsRead',
  async (_, { rejectWithValue }) => {
    try {
      await api.post(API_ROUTES.NOTIFICATIONS.READ_ALL);
      return { readAt: new Date().toISOString() };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to mark notifications as read'
      );
    }
  }
);

export const performNotificationAction = createAsyncThunk<
  { notificationId: string; updatedNotification: Notification },
  { notification: Notification; actionType: NotificationActionType }
>(
  'notifications/performNotificationAction',
  async ({ notification, actionType }, { rejectWithValue }) => {
    try {
      const updated = await api.post<Notification>(
        API_ROUTES.NOTIFICATIONS.ACTION(notification.id),
        {
          actionType,
        }
      );

      return {
        notificationId: notification.id,
        updatedNotification: applyNotificationActionState(notification, actionType, updated),
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to apply action');
    }
  }
);

export const deleteNotificationsBulk = createAsyncThunk<
  { deletedIds: string[] },
  { ids: string[] }
>('notifications/deleteNotificationsBulk', async ({ ids }, { rejectWithValue }) => {
  try {
    const response = await api.post<{ deletedIds?: string[] }>(
      API_ROUTES.NOTIFICATIONS.BULK_DELETE,
      {
        ids,
      }
    );

    const deletedIds = Array.isArray(response?.deletedIds) ? response.deletedIds : ids;
    return { deletedIds };
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to delete notifications'
    );
  }
});

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    upsertNotification(state, action: PayloadAction<Notification>) {
      const existingIndex = state.items.findIndex(
        notification => notification.id === action.payload.id
      );

      if (existingIndex === -1) {
        state.items = sortNotifications([action.payload, ...state.items]);
        return;
      }

      state.items[existingIndex] = {
        ...state.items[existingIndex],
        ...action.payload,
      };
      state.items = sortNotifications(state.items);
    },
    updateNotification(
      state,
      action: PayloadAction<{ notificationId: string; changes: Partial<Notification> }>
    ) {
      const notification = state.items.find(item => item.id === action.payload.notificationId);
      if (!notification) {
        return;
      }

      Object.assign(notification, action.payload.changes);
      state.items = sortNotifications(state.items);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchNotifications.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.items = sortNotifications(action.payload);
        state.isLoading = false;
        state.error = null;
        state.lastFetched = Date.now();
        state.lastRequestedLimit = action.meta.arg?.limit ?? DEFAULT_NOTIFICATION_LIMIT;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed';
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const notification = state.items.find(item => item.id === action.payload.notificationId);
        if (!notification) {
          return;
        }

        notification.status = 'read';
        notification.readAt = action.payload.readAt;
      })
      .addCase(markAllNotificationsRead.fulfilled, (state, action) => {
        state.items = state.items.map(notification => {
          if (notification.status !== 'unread') {
            return notification;
          }

          return {
            ...notification,
            status: 'read',
            readAt: action.payload.readAt,
          };
        });
      })
      .addCase(performNotificationAction.fulfilled, (state, action) => {
        const notification = state.items.find(item => item.id === action.payload.notificationId);
        if (!notification) {
          return;
        }

        Object.assign(notification, action.payload.updatedNotification);
        state.items = sortNotifications(state.items);
      })
      .addCase(deleteNotificationsBulk.fulfilled, (state, action) => {
        const deletedIds = new Set(action.payload.deletedIds);
        state.items = state.items.filter(notification => !deletedIds.has(notification.id));
        state.items = sortNotifications(state.items);
      });
  },
});

export const { upsertNotification, updateNotification } = notificationSlice.actions;

export default notificationSlice.reducer;
